import { ComponentPropsWithoutRef, ForwardedRef, forwardRef } from 'react';
import { Button, ButtonProps } from '@givebutter/primitives/button';
import { useMediaQuery } from '@givebutter/react-util';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';

interface RequiredItemRootProps extends ComponentPropsWithoutRef<'div'> {}
const RequiredItemRoot = ({ children, className, ...rest }: RequiredItemRootProps) => {
    return (
        <div
            className={twMerge(
                'flex w-full flex-col gap-6 overflow-x-auto p-6 sm:flex-row',
                className,
            )}
            {...rest}
        >
            {children}
        </div>
    );
};

interface RequiredItemTextContentProps extends ComponentPropsWithoutRef<'div'> {}
const RequiredItemTextContent = ({
    children,
    className,
    ...rest
}: RequiredItemTextContentProps) => {
    return (
        <div
            className={twMerge('flex w-full flex-col justify-between sm:min-w-64', className)}
            {...rest}
        >
            {children}
        </div>
    );
};

interface RequiredItemTitleProps extends ComponentPropsWithoutRef<'p'> {}
const RequiredItemTitle = ({ children, className, ...rest }: RequiredItemTitleProps) => {
    return (
        <p className={twMerge('text-sm font-medium text-gray-700', className)} {...rest}>
            {children}
        </p>
    );
};

interface RequiredItemDescriptionProps extends ComponentPropsWithoutRef<'p'> {}
const RequiredItemDescription = ({
    children,
    className,
    ...rest
}: RequiredItemDescriptionProps) => {
    return (
        <p className={twMerge('text-sm text-gray-600', className)} {...rest}>
            {children}
        </p>
    );
};

interface RequiredItemDurationProps extends ComponentPropsWithoutRef<'p'> {}
const RequiredItemDuration = ({ children, className, ...rest }: RequiredItemDurationProps) => {
    return (
        <p
            className={twMerge(
                'rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700',
                className,
            )}
            {...rest}
        >
            {children}
        </p>
    );
};

interface RequiredItemPrimaryButtonProps extends Omit<ButtonProps, 'variant' | 'color' | 'size'> {}
const RequiredItemPrimaryButton = forwardRef(
    (
        { children, className, ...rest }: RequiredItemPrimaryButtonProps,
        forwardedRef: ForwardedRef<HTMLButtonElement>,
    ) => {
        return (
            <Button
                ref={forwardedRef}
                variant="outline"
                color="neutral"
                size="sm"
                className={twMerge('rounded-lg text-gray-700', className)}
                {...rest}
            >
                {children}
            </Button>
        );
    },
);

interface RequiredItemSecondaryButtonProps
    extends Omit<ButtonProps, 'variant' | 'color' | 'size'> {}
const RequiredItemSecondaryButton = forwardRef(
    (
        { children, className, ...rest }: RequiredItemSecondaryButtonProps,
        forwardedRef: ForwardedRef<HTMLButtonElement>,
    ) => {
        return (
            <Button
                ref={forwardedRef}
                variant="transparent"
                color="neutral"
                size="sm"
                className={twMerge(
                    'rounded-lg font-medium text-gray-600 hover:bg-gray-100',
                    className,
                )}
                {...rest}
            >
                {children}
            </Button>
        );
    },
);

interface RequiredItemCompletedIndicatorProps
    extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {}
const RequiredItemCompletedIndicator = ({
    className,
    ...rest
}: RequiredItemCompletedIndicatorProps) => {
    return (
        <div className={twMerge('flex items-center gap-2', className)} {...rest}>
            <FontAwesomeIcon icon={faCircleCheck} className={'text-success-500 size-4'} />
            <p className="text-sm font-medium text-gray-700">Completed</p>
        </div>
    );
};

interface RequiredItemImageProps extends Omit<ComponentPropsWithoutRef<'img'>, 'src'> {
    desktopSrc: string;
    mobileSrc?: string;
}
const RequiredItemImage = ({
    desktopSrc,
    mobileSrc,
    className,
    ...rest
}: RequiredItemImageProps) => {
    const isMobile = useMediaQuery(`(max-width: 640px)`);
    return (
        <img
            className={twMerge(
                'h-48 w-full shrink-0 rounded-lg bg-gray-100 object-cover sm:max-w-[200px]',
                className,
            )}
            alt=""
            {...rest}
            src={isMobile && mobileSrc ? mobileSrc : desktopSrc}
        />
    );
};

const Root = RequiredItemRoot;
const TextContent = RequiredItemTextContent;
const Title = RequiredItemTitle;
const Description = RequiredItemDescription;
const PrimaryButton = RequiredItemPrimaryButton;
const SecondaryButton = RequiredItemSecondaryButton;
const CompletedIndicator = RequiredItemCompletedIndicator;
const Image = RequiredItemImage;

export {
    RequiredItemRoot,
    RequiredItemTextContent,
    RequiredItemTitle,
    RequiredItemDescription,
    RequiredItemDuration,
    RequiredItemPrimaryButton,
    RequiredItemSecondaryButton,
    RequiredItemCompletedIndicator,
    RequiredItemImage,
    //
    Root,
    TextContent,
    Title,
    Description,
    PrimaryButton,
    SecondaryButton,
    CompletedIndicator,
    Image,
};
